import { useEffect, useState } from 'react';

import { Center, Text } from '@chakra-ui/react';
import { t } from 'i18next';
import { first, isEmpty } from 'lodash';
import { useQueryClient } from 'react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { useFilePicker } from 'use-file-picker';
import { ArrowRightSmall } from '@peacecover/icon';

import Page from '../../components/Page';
import CommonDialog from '../../components/alerts/CommonDialog';
import updateUserProfileV1 from '../../components/api/mutations/updateUserProfileV1';
import useUserProfileQuery from '../../components/api/queries/useUserProfileQuery';
import AddUserProfileV1BottomSheet from '../../components/bottomSheet/AddUserProfileV1BottomSheet';
import PrimaryInput from '../../components/input/PrimaryInput';
import ProfileAvatar from '../../components/thumbnails/ProfileAvatar';

export default function TradingUserProfileSettingsPage() {
  const params = useParams();
  const queryClient = useQueryClient();

  const [userProfile, setUserProfile] = useState();
  const defaultUserProfile = useUserProfileQuery(params.user_profile_id);

  useEffect(() => {
    if (!isEmpty(defaultUserProfile)) {
      setUserProfile(defaultUserProfile);
    }
  }, [defaultUserProfile]);

  const onUpdatedUserProfile = (userProfile) => {
    queryClient.invalidateQueries('userProfile');
    setUserProfile(userProfile);
  };

  return (
    <Page
      title={t('TradingUserProfileSettingsPage.pageTitle')}
      showTitle={true}
      showBackButton={true}
    >
      <ProfileImageSection userProfile={userProfile} onUpdatedUserProfile={onUpdatedUserProfile} />
      <NicknameRow userProfile={userProfile} onUpdatedUserProfile={onUpdatedUserProfile} />
      <DescriptionRow userProfile={userProfile} onUpdatedUserProfile={onUpdatedUserProfile} />
      <ManageTradingCardsRow />
      <ManageTradingHistoriesRow />
      <ManageTradingReviewsRow />
      {<AddUserProfileV1BottomSheet userProfile={userProfile} />}
    </Page>
  );
}

const ProfileImageSection = ({ userProfile, onUpdatedUserProfile }) => {
  const { openFilePicker, filesContent } = useFilePicker({
    readAs: 'DataURL',
    accept: 'image/jpeg,image/png,image/webp,image/bmp,image/tiff,image/x-icon,image/heic',
    multiple: false
  });

  useEffect(() => {
    const updateProfile = async () => {
      const dataUrl = first(filesContent)?.content;
      if (!isEmpty(dataUrl)) {
        const response = await updateUserProfileV1({
          userProfileId: userProfile.userProfileId,
          dataUrl: dataUrl
        });
        if (response.ok) {
          const responseBody = await response.json();
          onUpdatedUserProfile(responseBody.userProfile);
        }
      }
    };
    updateProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filesContent]);

  return (
    <Center flexDirection={'column'} marginBottom={'20px'}>
      <ProfileAvatar
        profileImageUrl={userProfile?.thumbnailUrl}
        widthAndHeight={'72px'}
        svgWidthAndHeight={'36px'}
        padding={'18px'}
      />
      <button
        type="button"
        className="mt-4 w-full border-0 bg-transparent p-0 text-center text-[15px] font-medium leading-[18px] text-primary-light dark:text-primary-dark"
        onClick={openFilePicker}
      >
        {t('TradingUserProfileSettingsPage.changeProfileImage')}
      </button>
    </Center>
  );
};

const NicknameRow = ({ userProfile, onUpdatedUserProfile }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [nickname, setNickname] = useState(userProfile?.nickname);

  const onConfirmed = async () => {
    if (!isEmpty(nickname)) {
      const response = await updateUserProfileV1({
        userProfileId: userProfile.userProfileId,
        nickname: nickname
      });
      if (response.ok) {
        const responseBody = await response.json();
        onUpdatedUserProfile(responseBody.userProfile);
        setIsOpen(false);
      }
    }
  };

  return (
    <>
      <button
        type="button"
        className="flex min-h-[66px] w-full items-center border-0 border-b-[1px] border-separator-light-1 bg-transparent pt-[14px] pb-[14px] text-left dark:border-separator-dark-1"
        onClick={() => setIsOpen(true)}
      >
        <span className="min-w-0 flex-1">
          <span className="block text-left text-[15px] font-medium leading-[18px] text-main-light-1 dark:text-main-dark-1">
            {t('TradingUserProfileSettingsPage.nickname')}
          </span>
        </span>
        <span className="flex shrink-0 items-center">
          <span className="block text-left text-[15px] font-medium leading-[18px]">
            {userProfile?.nickname}
          </span>
        </span>
      </button>
      {isOpen && (
        <CommonDialog
          isOpen={isOpen}
          onClose={() => setIsOpen(false)}
          title={t('TradingUserProfileSettingsPage.changeNicknameDialogTitle')}
          children={
            <>
              <PrimaryInput
                defaultValue={nickname || userProfile?.nickname}
                maxLength={20}
                placeholder={t('TradingUserProfileSettingsPage.placeHolderNickname')}
                onChangeText={(e) => setNickname(e.target.value)}
                style={{ marginTop: '16px' }}
              />
              <div style={{ textAlign: 'right' }}>
                {(nickname || userProfile?.nickname) && (
                  <Text value={nickname || userProfile?.nickname} fontSize={'13px'}>
                    {(nickname || userProfile?.nickname).length}/20
                  </Text>
                )}
              </div>
            </>
          }
          onClickDialogButton={() => onConfirmed()}
        />
      )}
    </>
  );
};

const DescriptionRow = ({ userProfile, onUpdatedUserProfile }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [description, setDescription] = useState(userProfile?.description);

  const onConfirmed = async () => {
    if (!isEmpty(description)) {
      const response = await updateUserProfileV1({
        userProfileId: userProfile.userProfileId,
        description: description
      });
      if (response.ok) {
        const responseBody = await response.json();
        onUpdatedUserProfile(responseBody.userProfile);
        setIsOpen(false);
      }
    }
  };

  return (
    <>
      <button
        type="button"
        className="flex min-h-[66px] w-full items-center border-0 border-b-[1px] border-separator-light-1 bg-transparent pt-[14px] pb-[14px] text-left dark:border-separator-dark-1"
        onClick={() => setIsOpen(true)}
      >
        <span className="min-w-0 flex-1">
          <span className="block text-left text-[15px] font-medium leading-[18px] text-main-light-1 dark:text-main-dark-1">
            {t('TradingUserProfileSettingsPage.description')}
          </span>
        </span>
        <span className="flex shrink-0 items-center">
          {isEmpty(userProfile?.description) ? (
            <span className="block text-left text-[15px] font-medium leading-[18px] text-primary-light dark:text-primary-dark">
              {t('TradingUserProfileSettingsPage.add')}
            </span>
          ) : (
            <span className="block text-left text-[15px] font-medium leading-[18px] text-main-light-1 dark:text-main-dark-1">
              {userProfile?.description}
            </span>
          )}
        </span>
      </button>
      {isOpen && (
        <CommonDialog
          isOpen={isOpen}
          onClose={() => setIsOpen(false)}
          title={t('TradingUserProfileSettingsPage.changeDescriptionDialogTitle')}
          children={
            <>
              <PrimaryInput
                defaultValue={description || userProfile?.description}
                maxLength={50}
                placeholder={t('TradingUserProfileSettingsPage.placeHolderDescription')}
                onChangeText={(e) => setDescription(e.target.value)}
                style={{ marginTop: '16px' }}
              />
              <div style={{ textAlign: 'right' }}>
                {(description || userProfile?.description) && (
                  <Text value={description || userProfile?.description} fontSize={'13px'}>
                    {(description || userProfile?.description).length}/50
                  </Text>
                )}
              </div>
            </>
          }
          onClickDialogButton={() => onConfirmed()}
        />
      )}
    </>
  );
};

const ManageTradingCardsRow = () => {
  const navigate = useNavigate();
  return (
    <button
      type="button"
      className="flex min-h-[66px] w-full items-center border-0 border-b-[1px] border-separator-light-1 bg-transparent pt-[14px] pb-[14px] text-left dark:border-separator-dark-1"
      onClick={() => navigate('/my-trading-photo-cards')}
    >
      <span className="min-w-0 flex-1">
        <span className="block text-left text-[15px] font-medium leading-[18px] text-main-light-1 dark:text-main-dark-1">
          {t('TradingUserProfileSettingsPage.manageTradingCards')}
        </span>
      </span>
      <span className="flex shrink-0 items-center">
        <ArrowRightSmall size={20} className="text-text-sub-lv1" />
      </span>
    </button>
  );
};

const ManageTradingHistoriesRow = () => {
  const navigate = useNavigate();
  return (
    <button
      type="button"
      className="flex min-h-[66px] w-full items-center border-0 border-b-[1px] border-separator-light-1 bg-transparent pt-[14px] pb-[14px] text-left dark:border-separator-dark-1"
      onClick={() => navigate('/my-photo-cards-market')}
    >
      <span className="min-w-0 flex-1">
        <span className="block text-left text-[15px] font-medium leading-[18px] text-main-light-1 dark:text-main-dark-1">
          {t('TradingUserProfileSettingsPage.manageTradingHistories')}
        </span>
      </span>
      <span className="flex shrink-0 items-center">
        <ArrowRightSmall size={20} className="text-text-sub-lv1" />
      </span>
    </button>
  );
};

const ManageTradingReviewsRow = () => {
  return (
    <div className="flex min-h-[66px] border-b-[1px] border-separator-light-1 pt-[14px] pb-[14px] dark:border-separator-dark-1">
      <div className="grow content-center">
        <p className="text-left text-[15px] font-medium leading-[18px] text-main-light-1 dark:text-main-dark-1">
          {t('TradingUserProfileSettingsPage.manageTradingReviews')}
        </p>
      </div>
      <div className="flex shrink items-center">
        <p className="text-left text-[15px] font-medium leading-[18px] text-primary-light dark:text-primary-dark">
          {'Coming soon :)'}
        </p>
      </div>
    </div>
  );
};
